/**
 * Session 登录态
 * @author jasonelchen
 */
import { useCallback, useMemo, useState, useEffect } from 'react'
import { message } from 'antd'
import { stringify } from 'qs'
import { useHistory } from 'react-router-dom'
import { useObject, useService } from '@hooks'
import { CompanyInfo, ModuleInfo, StaffisAdmin, StaffisAssign, StaffStatus, UserInfo } from '@types'
import { clearTccToken, getModuleCompanyMapNoSession, getUserInfo as getUserInfoApi, login } from '@services/session'
import { remove, get } from 'js-cookie'
import { ORIGIN_TCC_TOKEN, moduleId } from '@configs'
import { injectTcccWebSDK } from 'src/library/t3c'
import { getT3cSdkUrl, getCallDuration } from '@services/t3c'
import { getHotline400Config } from '@services/lead/hotline'
import moment from 'moment'
import { uniqBy } from 'lodash'
import { ASRItem, CallInfo, CallStatusString } from 'src/types/ai-assistant'
export function useSession () {
  const [logging, setLogging] = useState(true)
  const { push } = useHistory()

  const [, moduleRes, , forceRequestModuleCompanyMap] = useService(getModuleCompanyMapNoSession)
  // 这里转换一下数据结构，得到所有的模块列表和公司列表
  const companyList: CompanyInfo[] = useMemo(() => Array.from(moduleRes?.list || []), [moduleRes])
  const moduleList: ModuleInfo[] = useMemo(() => uniqBy(companyList.map(company => {
    const { belongModule, belongModuleName } = company
    return { belongModule, belongModuleName }
  }), 'belongModule'), [companyList])

  const [userInfo, setUserInfo] = useObject<UserInfo>({
    salesName: '',
    salesNameCh: '',
    avatar: '',
    isAdmin: StaffisAdmin.NOTADMIN,
    isAssign: StaffisAssign.NO,
    belongModule: -1,
    companyId: -1,
    createTime: '',
    id: -1,
    lastModifier: '',
    leader: '',
    openid: '-1',
    poolNum: -1,
    qidianWorkNumber: '-1',
    status: StaffStatus.OFFDUTY,
    updateTime: '',
    salesPhone: '',
    weworkShortChain: '',
    useT3C: false,
    useMeeting: false,
    useNewT3CCallBar: false,
  })

  // 当前模块下所有公司
  const currCompanyList: CompanyInfo[] = useMemo(() => companyList.filter(company => company.belongModule === userInfo.belongModule), [companyList, userInfo.belongModule])

  const [t3cReady, setT3cReady] = useState(false)
  const [t3cSdkReady, setT3cSdkReady] = useState(false)
  const [t3cSdkReadyTime, setT3cSdkReadyTime] = useState('')
  const [t3cSdkUrl, setT3cSdkUrl] = useState('')
  const [t3cErrorMsg, setT3cErrorMsg] = useState('')
  const [t3cfailed, setT3cFailed] = useState(false)
  const [curTabUUID, setCurTabUUID] = useState('')
  const [hotline400Config, setHotline400Config] = useState({ list: [] })
  const [showCallWorkbench, setShowCallWorkbench] = useState(false) // 是否显示通话工作台
  const [isCallMute, setIsCallMute] = useState(false) // 是否静音
  const [isCallHold, setIsCallHold] = useState(false) // 是否挂起
  const [sessionId, setSessionId] = useState('')
  const [asrList, setAsrList] = useState<ASRItem[]>([]) // 语音转文本列表
  const [callStatus, setCallStatus] = useState<CallStatusString>('idle') // 通话状态
  const [callInfo, setCallInfo] = useState<CallInfo>({
    calleeLocation: '',
    calleePhoneNumberText: '',
    callerPhoneNumberText: '',
    calleeName: '',
    recordId: undefined,
  })
  const [callDuration, setCallDuration] = useObject<{ callDuration: string; callTime: number }>({ callDuration: '00:00', callTime: 0 })
  const getTodayCallDuration = async () => {
    const [res, err] = await getCallDuration()
    if (err) {
      message.error(err.message)
      return
    }
    const duration = moment.utc(res?.callDuration * 1000).format(res?.callDuration < 3600 ? 'mm:ss' : 'HH:mm:ss')
    setCallDuration({
      callDuration: duration,
      callTime: res.callTime,
    })
  }
  // 拉取用户信息
  const getUserInfo = useCallback(async () => {
    const [res, err] = await getUserInfoApi()

    if (err) {
      throw err
    }

    setLogging(false)
    setUserInfo(res)
    return res
  }, [])

  // 登录
  const loginWithCode = useCallback(async (code: string, moduleId: number, appId: string, agentId: number, tccCompany: number) => {
    // 清理可能遗留的上次模拟登录的cookie
    remove(ORIGIN_TCC_TOKEN)

    const [, err] = await login(code, moduleId, appId, agentId, tccCompany)

    if (err) {
      throw err
    }

    // 拉取用户信息
    return getUserInfo()
  }, [])

  // 登录方式枚举
  const LOGIN_TYPES = {
    WECHAT_QUICK: 'wechat_quick', // 企微快速登录（模块 7、8、13）
    CUSTOM_SSO: 'custom_sso', // 自定义 SSO 登录（模块 12）
    LEGACY: 'legacy' // 传统登录方式（其他模块）
  } as const

  // 根据模块 ID 获取登录方式
  const getLoginType = useCallback((currentModuleId: number) => {
    // 模块 7、8、13：企微快速登录
    if ([moduleId.MID_LONG_TAIL_ID, moduleId.MID_LONG_TAIL_CLEAN_ID, moduleId.SASS_ID].includes(currentModuleId)) {
      return LOGIN_TYPES.WECHAT_QUICK
    }

    // 模块 12：自定义 SSO 登录
    // if (currentModuleId === moduleId.COC_TURN_ID) {
    //   return LOGIN_TYPES.CUSTOM_SSO
    // }

    // 其他模块：传统登录方式
    return LOGIN_TYPES.LEGACY
  }, [])

  // 跳转到企业微信登录页面
  const jumpToWorkWechat = useCallback((appId: string, agentId: string, callbackUrl?: string) => {
    const { origin } = window.location

    // 从 cookie 中获取当前选择的模块
    const currentModuleId = parseInt(get('tcc-module') || '0')

    const loginType = getLoginType(currentModuleId)

    switch (loginType) {
      case LOGIN_TYPES.WECHAT_QUICK: {
        // 模块 7、8、13：企微快速登录，添加 login_type 参数
        const query = stringify({
          login_type: 'CorpApp',
          appid: appId,
          agentid: agentId,
          redirect_uri: `${origin}/login`,
          state: stringify({ callbackUrl, currentModuleId }),
        })
        window.location.href = `https://login.work.weixin.qq.com/wwlogin/sso/login?${query}`
        break
      }

      // case LOGIN_TYPES.CUSTOM_SSO: {
      //   // 模块 12：自定义 SSO 登录（预留，待实现）
      //   // TODO: 实现模块 12 的自定义 SSO 登录逻辑
      //   console.warn('模块 12 的自定义 SSO 登录逻辑待实现')

      //   // 临时使用传统登录方式
      //   const query = stringify({
      //     appid: appId,
      //     agentid: agentId,
      //     redirect_uri: `${origin}/login`,
      //     state: stringify({ callbackUrl, currentModuleId }),
      //   })
      //   window.location.href = `https://open.work.weixin.qq.com/wwopen/sso/qrConnect?${query}`
      //   break
      // }

      case LOGIN_TYPES.LEGACY:
      default: {
        // 其他模块：传统登录方式
        const query = stringify({
          appid: appId,
          agentid: agentId,
          redirect_uri: `${origin}/login`,
          state: encodeURIComponent(stringify({ callbackUrl, currentModuleId })),
        })

        window.location.href = `https://open.work.weixin.qq.com/wwopen/sso/qrConnect?${query}`
        break
      }
    }
  }, [getLoginType])

  // 登出
  const logout = useCallback(async () => {
    // 静默失败
    await clearTccToken()
    message.success('退出登录成功')
    return push(`/login?${stringify({ callbackUrl: window.location.pathname + window.location.search })}`)
  }, [])

  const initT3cSdk = async (params?: { refresh: boolean }) => {
    setT3cReady(false)
    const [sdkUrl, err] = await getT3cSdkUrl(params)
    if (sdkUrl) {
      injectTcccWebSDK(sdkUrl.sdkUrl, () => {
        setT3cSdkUrl(sdkUrl.sdkUrl)
        setT3cSdkReadyTime(new Date().toLocaleString())
        setT3cSdkReady(true)
        // 加载JS SDK文件成功，此时可使用全局变量"tccc"
        window.tccc.UI.hidefloatButton()
        !isPermissableBelongModule([moduleId.MID_LONG_TAIL_CLEAN_ID]) && window.tccc.UI.hideWorkbench()
        window.tccc.on(window.tccc.events.ready, ({ tabUUID }) => {
          setCurTabUUID(tabUUID)
          setT3cReady(true)
          setT3cFailed(false)
          setT3cErrorMsg('')
          console.log('T3C SDK 初始化成功，当前客服状态：', window.tccc.Agent.getStatus(), 'tabUUID:', tabUUID)
        })
        window.tccc.on(window.tccc.events.tokenExpired, ({ message: errMsg, tabUUID }) => {
          console.error('初始化失败:', errMsg)
          message.error(`TCCC error: ${errMsg}。tabUUID: ${curTabUUID}; userId: ${userInfo?.email};当前时间: ${new Date().toLocaleString()}`)
          setT3cErrorMsg(errMsg)
          setT3cReady(true)
          setT3cFailed(true)
          console.log('T3C SDK 初始化失败. tabUUID:', tabUUID)
        })

        window.tccc.on(window.tccc.events.callOuted, (res) => {
          setCallStatus('calling')
          console.log('外呼成功事件 window.tccc.events.callOuted', res)
          setSessionId(res.sessionId)
          setCallInfo(info => {
            return {
              ...info,
              calleeLocation: res.calleeLocation,
              calleePhoneNumberText: res.calleePhoneNumberText,
              callerPhoneNumberText: res.callerPhoneNumberText,
              callTimestamp: 0,
            }
          })
          setShowCallWorkbench(true)
        })

        window.tccc.on(window.tccc.events.calloutAccepted, (res) => {
          setCallStatus('talking')
          console.log('外呼对方接听事件 window.tccc.events.calloutAccepted', res)
          setSessionId(res.sessionId)
          setShowCallWorkbench(true)
          setCallInfo((currentInfo) => {
            return {
              ...currentInfo,
              callTimestamp: res.timestamp,
            }
          })
        })

        window.tccc.on('muted', (res) => {
          setIsCallMute(true)
        })
        window.tccc.on(window.tccc.events.asr, handleAsrEvent)
        try {
          window.tccc.on(window.tccc.events.kickedOut, () => {
            message.error(`当前坐席在其他地方登录。tabUUID: ${curTabUUID}; userId: ${userInfo?.email};当前时间: ${new Date().toLocaleString()}`)
            console.log('当前坐席在其他地方登录')
          })
        } catch (error) {
          console.log('T3C SDK kickedOut error:', error)
        }
      }, userInfo)
    } else {
      message.error(err.message)
    }
  }
  const getHotline400ConfigOnce = async () => {
    const [config] = await getHotline400Config()
    setHotline400Config(config)
  }

  useEffect(() => {
    if (t3cReady) {
      isPermissableBelongModule([moduleId.SDR_CLEAN]) && userInfo.useNewT3CCallBar && window.tccc.UI.hideNotificationBar()
      window.tccc.on(window.tccc.events.userAccessed, (res) => {
        console.log('来电显示', res, 'hotline400Config:', hotline400Config)
        const result = hotline400Config?.list.find(item => item.hotline === res.calleePhoneNumber)
        // 如果来源是乐享或电子签，跳转到通用400
        if (res.tabUUID === curTabUUID && result && result.redirect === 1) {
          window.open(`/lead/hotline/clue-collect/400?qd_tel=${res.callerPhoneNumber}&qd_calll=t3c-${res.sessionId}&qd_kfext=${userInfo.email}&from=${result.from}`)
        }
      })
      window.tccc.on(window.tccc.events.sessionEnded, () => {
        console.log('通话结束')
        setAsrList([])
        setSessionId('')
        setIsCallMute(false)
        setIsCallHold(false)
        setCallStatus('idle')
        setShowCallWorkbench(false)
        setCallInfo({
          calleeLocation: '',
          calleePhoneNumberText: '',
          callerPhoneNumberText: '',
          calleeName: '',
          recordId: undefined,
        })
        setTimeout(() => {
          getTodayCallDuration()
        }, 2000)
        setTimeout(() => {
          getTodayCallDuration()
        }, 30000)
      })
    }
  }, [t3cReady, userInfo])

  const handleAsrEvent = useCallback((res) => {
    console.log('语音识别事件 window.tccc.events.asr.', res, res.result.slice_type)
    if (!res?.result?.voice_text_str?.trim()) {
      return
    }

    // 如果两个人同时讲话，A先触发，接着B消息触发，但是最终A消息最后结束，那应该把A的消息放到最下面展示
    setAsrList((currentList) => {
      const matchMessage = [...currentList].reverse().find(item => item.flow === res.flow)
      // 如果没有匹配到消息，则说明这是一条新进来的消息，直接添加到列表中
      if (!matchMessage) {
        return [...currentList, {
          flow: res.flow,
          message: res.result.voice_text_str,
          timestamp: res.result.asr_timestamp,
          sliceType: res.result.slice_type,
          index: res.result.index,
        }]
      } else {
        // 如果匹配到了一条消息，并且sliceType等于2，说明这条消息结束了，需要给列表增加一条新消息
        if (matchMessage.sliceType === 2) {
          return [...currentList, {
            flow: res.flow,
            message: res.result.voice_text_str,
            timestamp: res.result.asr_timestamp,
            sliceType: res.result.slice_type,
            index: res.result.index,
          }]
        } else {
          // 如果匹配到了一条消息，并且sliceType不等于2，说明这条消息还在继续，需要把这条消息的message更新为最新
          /**
           * 这里就要区分，新进来的消息是否是一条结束消息，如果是一条结束消息，则需要把这条消息的message更新为最新，否则则是更新这条消息的message
           */
          const modifyMessageIndex = currentList.findIndex(item => item.flow === matchMessage.flow && item.index === matchMessage.index)
          const newList = [...currentList]
          if (res.result.slice_type === 2) {
            newList.splice(modifyMessageIndex, 1)
            newList.push({
              message: res.result.voice_text_str,
              sliceType: res.result.slice_type,
              index: res.result.index,
              flow: res.flow,
              timestamp: res.result.asr_timestamp,
            })
          } else {
            newList[modifyMessageIndex] = {
              message: res.result.voice_text_str,
              sliceType: res.result.slice_type,
              index: res.result.index,
              flow: res.flow,
              timestamp: res.result.asr_timestamp,
            }
          }
          return newList
        }
      }
    })
  }, [])

  // 判断当前用户所属模块是否是参数所属模块
  const isPermissableBelongModule = useCallback((moduleIds: number[]) => {
    return moduleIds.some(moduleId => moduleId === userInfo.belongModule)
  }, [userInfo])
  return {
    logging,
    logged: !logging,
    userInfo,
    setUserInfo,
    moduleList, // 所有模块列表
    companyList, // 所有公司列表，注意，不是当前
    currCompanyList, // 当前所有公司列表
    loginWithCode,
    logout,
    jumpToWorkWechat,
    getUserInfo,
    forceRequestModuleCompanyMap, // 强制刷新moduleRes
    initT3cSdk,
    t3cReady,
    hotline400Config,
    setHotline400Config,
    getHotline400ConfigOnce,
    t3cfailed,
    t3cErrorMsg,
    callDuration,
    getTodayCallDuration,
    isPermissableBelongModule,
    curTabUUID,
    showCallWorkbench,
    setShowCallWorkbench,
    sessionId,
    asrList,
    isCallMute,
    setIsCallMute,
    isCallHold,
    setIsCallHold,
    callStatus,
    callInfo,
    setCallInfo,
    t3cSdkReady,
    t3cSdkReadyTime,
    t3cSdkUrl,
  }
}
